import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  signal,
  SimpleChanges
} from '@angular/core';
import { AspectRatioDirective } from '@newroom-connect/library/directives';
import { MediaObjectFit } from '@newroom-connect/library/types';

import { VideoComponent } from '../video/video.component';
import { AudioComponent } from '../audio/audio.component';
import { FileTypeComponent } from '../files/file-type/file-type.component';
import { ImageComponent } from '../image/image.component';

export type MediaPreviewType = 'audio' | 'video' | 'video-snippet' | 'image' | 'generic';

@Component({
  standalone: true,
  imports: [
    ImageComponent,
    VideoComponent,
    AudioComponent,
    FileTypeComponent,
    AspectRatioDirective
  ],
  selector: 'nrc-media-preview',
  templateUrl: './media-preview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./media-preview.component.scss']
})
export class MediaPreviewComponent implements OnChanges {
  @Input({ required: true }) public mimetype = '';
  @Input({ required: false }) public source?: string;
  @Input({ required: false }) public thumbnailSource?: string;
  @Input() public objectFit: MediaObjectFit = 'cover';
  @Input() public autoplay = false;
  @Input() public classList = '';
  @Input() public mediaPreviewTypes?: MediaPreviewType[];
  @Output() public loadComplete = new EventEmitter<void>();
  @Output() public videoPlayEvent = new EventEmitter<number | undefined>();
  @Output() public videoEndEvent = new EventEmitter<void>();
  @Output() public videoPauseEvent = new EventEmitter<number>();
  @Output() public videoSeekEvent = new EventEmitter<number>();
  @Output() public videoDuration = new EventEmitter<number | undefined>();

  public mediaPreviewType = signal<MediaPreviewType>('generic');

  /**
   * React to changes in component inputs.
   * @param changes Object containing all changed properties.
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['source'] || changes['mimetype']) {
      this.updateMediaType();
    }
  }

  /**
   * Update media preview type based on mimetype.
   */
  private updateMediaType(): void {
    this.mediaPreviewType.set(this.getMediaPreviewTypeFromMimetype(this.mimetype));
  }

  /**
   * Handle media load complete event.
   */
  public onMediaLoadComplete(): void {
    this.loadComplete.emit();
  }

  /**
   * Handle video end event and relay it upward.
   */
  public onVideoEnded(): void {
    this.videoEndEvent.emit();
  }

  /**
   * Handle video pause event and relay it upward.
   *
   * @param currentTime Current playback time in seconds.
   */
  public onVideoPaused(currentTime: number): void {
    this.videoPauseEvent.emit(currentTime);
  }

  /**
   * Handle video play event and relay it upward.
   *
   * @param currentTime Current playback time in seconds.
   */
  public onVideoPlay(currentTime: number): void {
    this.videoPlayEvent.emit(currentTime);
  }

  /**
   * Handle video seek event and relay it upward.
   *
   * @param currentTime Current playback time in seconds.
   */
  public onVideoSeeked(currentTime: number): void {
    this.videoSeekEvent.emit(currentTime);
  }

  /**
   * Handle video duration loaded event and relay it upward.
   *
   * @param duration Video duration in seconds.
   */
  public onVideoDuration(duration: number | undefined): void {
    this.videoDuration.emit(duration);
  }

  /**
   * Determine the media preview type based on the MIME type.
   * @param mimetype MIME type string to analyze.
   * @returns The appropriate media preview type.
   */
  private getMediaPreviewTypeFromMimetype(mimetype: string): MediaPreviewType {
    let mediaPreviewType: MediaPreviewType = 'generic';

    if (mimetype.startsWith('audio/') && this.isMediaPreviewTypeEnabled('audio')) {
      mediaPreviewType = 'audio';
    } else if (mimetype.startsWith('video/')) {
      if (this.isMediaPreviewTypeEnabled('video')) {
        mediaPreviewType = 'video';
      } else if (this.isMediaPreviewTypeEnabled('video-snippet')) {
        mediaPreviewType = 'video-snippet';
      }
    } else if (mimetype.startsWith('image/') && this.isMediaPreviewTypeEnabled('image')) {
      mediaPreviewType = 'image';
    }

    return mediaPreviewType;
  }

  /**
   * Check, if the given media preview type is enabled.
   * If no media preview types are available as input, all are enabled.
   *
   * @param mediaPreviewType The media preview type to check for enabled status.
   *
   * @returns Boolean indicating if the media preview type is enabled.
   */
  private isMediaPreviewTypeEnabled(mediaPreviewType: MediaPreviewType): boolean {
    if (!this.mediaPreviewTypes) {
      return true;
    }

    return this.mediaPreviewTypes.includes(mediaPreviewType);
  }
}
